import { ReactNode } from 'react';
import { Alert, Badge, type MantineSize, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

/**
 * The portal's non-chat surfaces ship on placeholder figures until the data is wired. On 21 Sep
 * the client said the dummy numbers looked real, so every figure-bearing block (KPI, chart, table,
 * download) carries this badge, and every such page opens with the notice below. Real entity
 * names are fine; the figures are what must never pass for data.
 */

const DEFAULT_TOOLTIP =
  'These figures are placeholders for design review, not H2 data. Real data arrives with the CMS integration.';

export type IllustrativeBadgeProps = {
  /** @desc Badge text — defaults to "Illustrative data". */
  label?: string;
  size?: MantineSize;
};

export function IllustrativeBadge({
  label = 'Illustrative data',
  size = 'xs',
}: IllustrativeBadgeProps) {
  return (
    <Tooltip label={DEFAULT_TOOLTIP} multiline w={260} withArrow>
      <Badge size={size} variant="light" color="yellow" radius="sm" tt="none" fw={500}>
        {label}
      </Badge>
    </Tooltip>
  );
}

export type IllustrativeNoticeProps = {
  title?: string;
  /** @desc Body text — defaults to the standard placeholder-data explanation. */
  children?: ReactNode;
};

/** @desc A page-top banner, once per page, explaining that the figures are placeholders. */
export function IllustrativeNotice({
  title = 'Illustrative data',
  children = 'Every figure on this page is a placeholder for design review — not H2 market data. Names of companies and markets are real; numbers are not.',
}: IllustrativeNoticeProps) {
  return (
    <Alert
      variant="light"
      color="yellow"
      radius="md"
      title={title}
      icon={<IconInfoCircle size={18} />}
    >
      {children}
    </Alert>
  );
}
